import type { EmotionDetectorConfig, EmotionFeatureRule, EmotionLabel } from '../../types';
import { avgKeys, clamp } from '../../LocalEmotionUtils';
import { resolveEmotionRuleConfigs } from './EmotionDetectorRegistry';

export interface EmotionScore {
  emotion: EmotionLabel;
  score: number;
  confidence: number;
  valence: number;
  arousal: number;
}

type FACSRule = EmotionFeatureRule;

const EYE_AU_KEYS = new Set<string>([
  'eyeSquintLeft', 'eyeSquintRight',
  'eyeWideLeft', 'eyeWideRight',
  'eyeLookDownLeft', 'eyeLookDownRight',
  'browInnerUp',
  'browOuterUpLeft', 'browOuterUpRight',
  'browDownLeft', 'browDownRight',
]);

const SURPRISE_PENALTY_KEYS = ['browDownLeft', 'browDownRight'];

const STABILITY_KEYS = [
  'browDownLeft', 'browDownRight', 'eyeSquintLeft', 'eyeSquintRight',
  'mouthSmileLeft', 'mouthSmileRight', 'mouthFrownLeft', 'mouthFrownRight',
  'jawOpen', 'eyeWideLeft', 'eyeWideRight', 'browInnerUp',
];

function getBaselineAdjustedThreshold(
  baseline: any | null,
  rule: FACSRule,
  emotion?: string,
): number {
  if (!baseline) return rule.threshold;

  const groupDelta = (group: string | null): number => {
    if (!group) return 0;
    const bgroup = baseline[group] as { p10: number; p90: number } | undefined;
    if (!bgroup || typeof bgroup.p90 !== 'number') return 0;
    return (bgroup.p90 - bgroup.p10) * 0.5;
  };

  let additive = 0;
  if (rule.keys.some(k => ['mouthSmileLeft', 'mouthSmileRight'].includes(k))) additive = Math.max(additive, groupDelta('smile'));
  if (rule.keys.some(k => ['mouthFrownLeft', 'mouthFrownRight'].includes(k))) additive = Math.max(additive, groupDelta('frown'));
  if (rule.keys.some(k => ['browDownLeft', 'browDownRight'].includes(k))) additive = Math.max(additive, groupDelta('browDown'));
  if (rule.keys.some(k => ['eyeWideLeft', 'eyeWideRight'].includes(k))) additive = Math.max(additive, groupDelta('eyeWide'));
  if (rule.keys.some(k => ['eyeSquintLeft', 'eyeSquintRight'].includes(k))) additive = Math.max(additive, groupDelta('eyeSquint'));
  if (rule.keys.some(k => ['browInnerUp', 'browOuterUpLeft', 'browOuterUpRight'].includes(k))) additive = Math.max(additive, groupDelta('browUp'));
  if (rule.keys.some(k => ['jawOpen'].includes(k))) additive = Math.max(additive, groupDelta('jawOpen'));
  if (rule.keys.some(k => ['noseSneerLeft', 'noseSneerRight'].includes(k))) additive = Math.max(additive, groupDelta('noseSneer'));

  if (additive === 0 && baseline.auRanges) {
    for (const key of rule.keys) {
      const range = baseline.auRanges[key];
      if (range && typeof range.p90 === 'number') {
        additive = Math.max(additive, (range.p90 - range.p10) * 0.5);
      }
    }
  }

  const adaptiveFactor = clamp(
    baseline.adaptiveConfig?.perEmotionAdaptiveFactors?.[emotion ?? 'happy']
      ?? (emotion === 'sad' ? 0.08 : emotion === 'surprise' ? 0.20 : 0.15),
    0.05,
    0.30,
  );
  return Math.min(0.9, rule.threshold + additive * adaptiveFactor);
}

function computeKeyReliabilityFactor(baseline: any | null, rule: FACSRule, eyeOccluded: boolean): number {
  if (!eyeOccluded) return 1;
  const usesEyeRegion = rule.keys.some(key => EYE_AU_KEYS.has(key));
  if (!usesEyeRegion) return 1;

  const ocRel = baseline?.eyeContextProfiles?.eyeOccluded?.auReliability;
  if (!ocRel) return 0.65;
  const rels = rule.keys.filter(key => EYE_AU_KEYS.has(key)).map(key => (ocRel[key] ?? 1));
  const avgKeyReliability = rels.length > 0 ? rels.reduce((a, b) => a + b, 0) / rels.length : 1;
  return 0.5 + 0.5 * clamp(avgKeyReliability, 0, 1);
}

export function scoreAllEmotions(
  baseline: any | null,
  bs: Record<string, number>,
  yawnDetected?: boolean,
  yawnScore?: number,
  eyeContext?: { eyeReliability?: number; eyeOccluded?: boolean; hasOccludedCalibration?: boolean },
  detectorConfig?: EmotionDetectorConfig,
): EmotionScore[] {
  const scores: EmotionScore[] = [];
  const emotionDefinitions = resolveEmotionRuleConfigs(detectorConfig?.emotions);
  const eyeReliability = eyeContext?.eyeReliability ?? 1;
  const eyeOccluded = !!eyeContext?.eyeOccluded;
  const hasOccludedCalibration = !!eyeContext?.hasOccludedCalibration;
  const eyeAUReliabilityFactor = eyeOccluded ? clamp(0.35 + eyeReliability * 0.65, 0.35, 1) : 1;

  const _log = typeof window !== 'undefined' && (window as any)._log ? (window as any)._log : console.log;
  const verboseLogs = typeof window !== 'undefined' && (window as any).__EIE_VERBOSE_LOGS === true;
  if (eyeOccluded && verboseLogs) {
    _log(`[EIE][EYE-SCORING] eyeOccluded=Y eyeRel=${eyeReliability.toFixed(3)} eyeAUFactor=${eyeAUReliabilityFactor.toFixed(3)} hasOccludedCal=${hasOccludedCalibration ? 'Y' : 'N'}`);
  }

  for (const definition of emotionDefinitions) {
    const emotion = definition.name;
    const rules = definition.rules;
    let weightedSum = 0;
    let totalWeight = 0;
    let activeCount = 0;

    for (const rule of rules) {
      const value = avgKeys(bs, rule.keys);
      const threshold = getBaselineAdjustedThreshold(baseline, rule, emotion);
      const usesEyeRegion = rule.keys.some(key => EYE_AU_KEYS.has(key));
      const scaleKeyFactor = computeKeyReliabilityFactor(baseline, rule, eyeOccluded);
      const effectiveWeight = rule.weight * (usesEyeRegion ? eyeAUReliabilityFactor : 1) * scaleKeyFactor;
      totalWeight += effectiveWeight;
      if (value >= threshold) {
        weightedSum += effectiveWeight * clamp((value - threshold) / (1 - threshold), 0, 1);
        activeCount++;
      }
    }

    if (emotion === 'surprise') {
      const browDown = avgKeys(bs, SURPRISE_PENALTY_KEYS);
      if (browDown > 0.15) weightedSum *= 0.3;
      const squint = avgKeys(bs, ['eyeSquintLeft', 'eyeSquintRight']);
      const eyeWide = avgKeys(bs, ['eyeWideLeft', 'eyeWideRight']);
      if (squint > 0.20 && eyeWide < 0.25) {
        weightedSum *= Math.max(0.10, 1 - squint * 2.5);
      }
    }

    let score = totalWeight > 0 ? weightedSum / totalWeight : 0;
    let auCoverage = rules.length > 0 ? activeCount / rules.length : 0;
    let confidence = clamp(score * 0.6 + auCoverage * 0.4, 0, 1);

    if (emotion === 'fear') {
      const eyeWide = avgKeys(bs, ['eyeWideLeft', 'eyeWideRight']);
      const browDown = avgKeys(bs, ['browDownLeft', 'browDownRight']);
      const jawOpen = avgKeys(bs, ['jawOpen']);
      if (eyeWide < 0.18) { score *= 0.55; confidence *= 0.70; }
      if (browDown > 0.08 && eyeWide < 0.22) { score *= 0.65; confidence *= 0.80; }
      if (jawOpen > 0.42 && eyeWide < 0.20) { score *= 0.45; confidence *= 0.70; }
    }

    if (emotion === 'angry') {
      const jawOpen = avgKeys(bs, ['jawOpen']);
      const eyeWide = avgKeys(bs, ['eyeWideLeft', 'eyeWideRight']);
      const frown = avgKeys(bs, ['mouthFrownLeft', 'mouthFrownRight']);
      if (jawOpen > 0.48 && eyeWide < 0.20 && frown < 0.08) { score *= 0.68; confidence *= 0.75; }
    }

    if (emotion === 'sad') {
      const frown = avgKeys(bs, ['mouthFrownLeft', 'mouthFrownRight']);
      const browDown = avgKeys(bs, ['browDownLeft', 'browDownRight']);
      const smile = avgKeys(bs, ['mouthSmileLeft', 'mouthSmileRight']);
      const squint = avgKeys(bs, ['eyeSquintLeft', 'eyeSquintRight']);
      const mouthShrugLower = avgKeys(bs, ['mouthShrugLower']);

      const squintThresholdForAngerProfile = eyeOccluded ? 0.05 : 0.25;
      const angryProfile = browDown > 0.15 && squint > squintThresholdForAngerProfile && frown < 0.05 && mouthShrugLower < 0.28;
      const browDownContrib = angryProfile ? 0 : browDown * 0.35;
      const shrugCorroborated = frown > 0.03 || browDown > 0.08 || smile < -0.01;
      const shrugContrib = angryProfile ? 0 : shrugCorroborated ? Math.max(0, mouthShrugLower - 0.15) * 0.32 : Math.max(0, mouthShrugLower - 0.40) * 0.16;
      let frownWeight = 0.74;
      if (eyeOccluded) {
        const ocRel = baseline?.eyeContextProfiles?.eyeOccluded?.auReliability;
        if (ocRel) {
          const leftRel = ocRel['mouthFrownLeft'] ?? ocRel['mouthFrownRight'] ?? 0.6;
          const rightRel = ocRel['mouthFrownRight'] ?? ocRel['mouthFrownLeft'] ?? 0.6;
          const avgRel = clamp((leftRel + rightRel) / 2, 0, 1);
          frownWeight = clamp(0.48 + 0.26 * avgRel, 0.4, 0.85);
        } else { frownWeight = 0.50; }
      }
      const sadRamp = clamp(browDownContrib + (frown * frownWeight) + shrugContrib - (smile * 0.20), 0, 0.50);
      score = clamp(score + sadRamp, 0, 1);
      confidence = clamp(confidence + clamp((angryProfile ? 0 : browDown * 0.16) + frown * 0.28 + Math.max(0, mouthShrugLower - 0.20) * 0.18, 0, 0.40), 0, 1);
      auCoverage = Math.min(1, auCoverage + 0.12);
      if (eyeOccluded && !hasOccludedCalibration) {
        if (verboseLogs) _log(`[EIE][EYE-COMPENSATION][SAD] no occluded calibration yet: dampening sad score ${score.toFixed(3)} → ${(score * 0.95).toFixed(3)}`);
        score *= 0.95; confidence *= 0.97;
      }
    }

    if (emotion === 'angry') {
      const squint = avgKeys(bs, ['eyeSquintLeft', 'eyeSquintRight']);
      const browDown = avgKeys(bs, ['browDownLeft', 'browDownRight']);
      const frown = avgKeys(bs, ['mouthFrownLeft', 'mouthFrownRight']);
      const smile = avgKeys(bs, ['mouthSmileLeft', 'mouthSmileRight']);
      const mouthPress = avgKeys(bs, ['mouthPressLeft', 'mouthPressRight']);
      const mouthRoll = avgKeys(bs, ['mouthRollLower']);
      const lipTension = Math.max(mouthPress, mouthRoll);
      const jawForward = avgKeys(bs, ['jawForward']);

      const browAngryCoeff = eyeOccluded ? 0.36 : 0.20;
      const squintAngryCoeff = eyeOccluded ? 0.12 : 0.28;
      const angryRamp = clamp((squint * squintAngryCoeff) + (lipTension * 0.18) + (browDown * browAngryCoeff) - (smile * 0.25) - (frown * 0.10), 0, 0.30);
      score = clamp(score + angryRamp, 0, 1);
      const browConfCoeff = eyeOccluded ? 0.22 : 0.12;
      const squintConfCoeff = eyeOccluded ? 0.06 : 0.18;
      confidence = clamp(confidence + clamp((squint * squintConfCoeff) + (browDown * browConfCoeff) + (lipTension * 0.08), 0, 0.30), 0, 1);
      const weakAngryEvidence = eyeOccluded ? browDown < 0.10 : (squint < 0.28 || browDown < 0.12);
      if (weakAngryEvidence) { score *= eyeOccluded ? 0.82 : 0.72; confidence *= 0.80; }
      const mouthShrugLower = avgKeys(bs, ['mouthShrugLower']);
      if (mouthShrugLower > 0.30 && lipTension < 0.10) { score *= 0.60; confidence *= 0.70; }
      const smileDominant = smile > 0.22 && frown < 0.06 && browDown < 0.16;
      if (smileDominant) { score *= 0.35; confidence *= 0.55; }
      if (eyeOccluded) {
        const mouthEvidence = Math.max(mouthPress, mouthRoll);
        const browSupport = browDown > 0.12;
        const jawSupport = jawForward > 0.08;
        const nonEyeCorroborated = mouthEvidence > 0.10 || browSupport || jawSupport;
        if (verboseLogs) _log(`[EIE][EYE-COMPENSATION][ANGRY] eyeOcc=Y eyeRel=${eyeReliability.toFixed(3)} mouthEvidence=${mouthEvidence.toFixed(3)} jaw=${jawForward.toFixed(3)} browDown=${browDown.toFixed(3)} corroborated=${nonEyeCorroborated ? 'Y' : 'N'} score=${score.toFixed(3)}`);
        if (!nonEyeCorroborated) {
          const ocRel = baseline?.eyeContextProfiles?.eyeOccluded?.auReliability;
          let corroborationReliability = 1;
          if (ocRel) {
            const keys = ['mouthPressLeft','mouthPressRight','mouthRollLower','jawForward','browDownLeft','browDownRight'];
            const rels = keys.map(k => (ocRel[k] ?? 1));
            corroborationReliability = rels.reduce((a, b) => a + b, 0) / Math.max(1, rels.length);
          } else {
            corroborationReliability = 0.6;
          }
          const baseDamp = 0.65;
          const adaptive = baseDamp + (1 - baseDamp) * eyeReliability * (0.6 + 0.4 * clamp(corroborationReliability, 0, 1));
          const factor = clamp(adaptive, baseDamp, 1);
          if (verboseLogs) _log(`[EIE][EYE-COMPENSATION][ANGRY] not corroborated → damping angry score ${score.toFixed(3)} × ${factor.toFixed(3)} = ${(score * factor).toFixed(3)} (corrRel=${corroborationReliability.toFixed(3)})`);
          score *= factor;
          confidence *= clamp(0.65 + eyeReliability * 0.35 * clamp(corroborationReliability, 0.6, 1), 0.55, 0.98);
        }
      }
      auCoverage = Math.min(1, auCoverage + 0.10);
    }

    scores.push({ emotion, score, confidence, valence: definition.valence, arousal: definition.arousal });
  }

  if (yawnDetected) {
    const yawnScoreFinal = yawnScore !== undefined ? Math.max(0.7, yawnScore) : 0.7;
    scores.push({ emotion: 'yawn', score: yawnScoreFinal, confidence: 0.95, valence: -0.2, arousal: 0.15 });
  }

  return scores;
}

export { STABILITY_KEYS };

export default { scoreAllEmotions };
